import { ArraySchema, MapSchema, Schema, type, view } from "@colyseus/schema"

import { PlayerClass, PlayerDataClass } from "./player"
import { CardDataClass } from "./сard"
import { cardsPerPlayer, DeckSize } from "../../consts"
import { CardsOnTableClass } from "./cardsOnTable"
import { sortPlayerCards } from "../../utils"
import { GameMode } from "./mode"
import { MatchmakingSchema } from "./matchmaking"

export type GameStatus = "waiting" | "playing" | "ended"

export class GameState extends Schema {
  @type("number") bet!: number
  @type("number") betCurrency!: number
  @type("number") betDecimals!: number

  @type("boolean") isPrivate!: boolean

  @type("number") deckSize!: DeckSize

  @type("boolean") isAllowedToToss!: boolean

  @type("boolean") isFirstBeat!: boolean

  @type("number") createdAt!: number

  @type("number") minPlayers!: number
  @type("number") maxPlayers!: number

  @type("string") status!: GameStatus

  @type(CardDataClass) trump!: CardDataClass

  @type("number") timerTime!: number
  @type("number") timer!: number
  @type("string") timerKind!: string

  @type("number") cardsCount!: number

  @type("number") gameTimerEnd?: number
  @type("number") gameDuration!: number

  @type("string") gameId!: string

  @type("string") chatId?: string
  @type("string") chatTitle?: string
  @type("string") chatLang?: string

  @type(GameMode) mode = new GameMode()

  @view(0)
  @type([CardDataClass])
  availableCards = new ArraySchema<CardDataClass>()

  @view(0)
  @type([CardDataClass])
  usedCards = new ArraySchema<CardDataClass>()

  @type(CardsOnTableClass)
  thrownCards = new CardsOnTableClass()

  @type({ map: PlayerDataClass })
  players = new MapSchema<PlayerDataClass>()

  @type({ map: PlayerClass })
  visitors = new MapSchema<PlayerClass>()

  @type({ map: PlayerDataClass })
  lastPlayers = new MapSchema<PlayerDataClass>()

  @type(MatchmakingSchema) matchmaking = new MatchmakingSchema()

  checkPlayerPutBeat(player: PlayerDataClass): boolean {
    if (player.playerState === "defender")
      throw new Error("Defender can't say beat")

    if (player.playerState === null)
      throw new Error("Player should be left or attacker to say beat")

    return !!player?.isSaidBeat
  }

  putThrewCardsToDefender(defender: PlayerDataClass): number {
    let count = 0

    this.thrownCards.defendingCards.forEach((card) => {
      defender.addCard(card)
      count++
    })

    this.thrownCards.attackingCards.forEach((card) => {
      defender.addCard(card)
      count++
    })

    sortPlayerCards(defender, this.trump.cardSuit)

    defender.cardsCount = defender.cards.length

    return count
  }

  removeThrewCards(): void {
    this.thrownCards.defendingCards.clear()
    this.thrownCards.attackingCards.clear()
  }

  givePlayQuantityCards(player: PlayerDataClass): number {
    if (this.availableCards.length === 0) return 0

    if (!player.cards) throw new Error("Player cards is undefined")

    const quantity = cardsPerPlayer - player.cards.length

    if (quantity <= 0) return 0

    let given = 0
    for (let i = 0; i < quantity; i++) {
      const card = this.availableCards.shift()
      if (card) {
        player.addCard(card)

        given++
      }
    }

    player.cardsCount = player.cards.length

    sortPlayerCards(player, this.trump.cardSuit)

    this.cardsCount = this.availableCards.length

    return given
  }

  // Add this method to GameState class
  findNextActivePlayerId(
    currentPlayerId: string,
    steps: number
  ): string | undefined {
    const playersArray = Array.from(this.players.values())

    // Filter active players (not surrendered and not won)
    const activePlayers = playersArray.filter(
      (player) => !player.surrenderAt && !player.winAt
    )

    if (activePlayers.length === 0) return undefined

    // Create array of player IDs
    const playerIds = activePlayers.map((p) => p.info.id.toString())

    // Find current player's position
    const currentIndex = playerIds.indexOf(currentPlayerId)

    // If player not found among active players, return the first active player
    if (currentIndex === -1) return playerIds[0]

    // Calculate the next player index with wrap-around
    const nextIndex = (currentIndex + steps) % playerIds.length

    // Return the ID of the next player
    return playerIds[nextIndex]
  }
}

export type GameType = InstanceType<typeof GameState>
